<template>
  <section id="fan-photos" class="fan-photos">
    <div class="container">
      <div class="section-header">
        <h2>{{ $t('fanGallery.title') }}</h2>
        <div class="celtic-divider">
          <span></span>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" />
          </svg>
          <span></span>
        </div>
      </div>
      
      <p class="intro">{{ $t('fanGallery.intro') }}</p>
    
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('fanGallery.loading') }}</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ $t('fanGallery.error') }}</p>
        <button @click="loadPhotos" class="btn-retry">
          <i class="fas fa-sync"></i> {{ $t('fanGallery.retry') }}
        </button>
      </div>

      <div v-else-if="photos.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-images"></i>
        </div>
        <p>{{ $t('fanGallery.noPhotos') }}</p>
        <p>{{ $t('fanGallery.beFirst') }}</p>
      </div>

      <div v-else class="photos-grid">
        <div v-for="photo in photos" :key="photo.id" class="photo-item" @click="openLightbox(photo)">
          <div class="photo-image">
            <img :src="photo.imageUrl" :alt="photo.caption || $t('fanGallery.noCaption')" loading="lazy" />
          </div>
          <div class="photo-info">
            <p class="photo-caption">{{ photo.caption || $t('fanGallery.noCaption') }}</p>
            <p class="photo-meta" v-if="photo.concertDate || photo.concertLocation">
              <template v-if="photo.concertDate">{{ formatDate(photo.concertDate) }}</template>
              <template v-if="photo.concertDate && photo.concertLocation"> - </template>
              <template v-if="photo.concertLocation">{{ photo.concertLocation }}</template>
            </p>
          </div>
        </div>
      </div>

      <!-- Lightbox -->
      <div v-if="lightbox.visible" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="lightbox-close" @click="closeLightbox">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          
          <div class="lightbox-image-container">
            <img :src="lightbox.photo.imageUrl" :alt="lightbox.photo.caption || $t('fanGallery.noCaption')" class="lightbox-image" />
          </div>
          
          <div class="lightbox-info">
            <h3>{{ lightbox.photo.caption || $t('fanGallery.noCaption') }}</h3>
            <p v-if="lightbox.photo.concertDate || lightbox.photo.concertLocation">
              <template v-if="lightbox.photo.concertDate">{{ formatDate(lightbox.photo.concertDate) }}</template>
              <template v-if="lightbox.photo.concertDate && lightbox.photo.concertLocation"> - </template>
              <template v-if="lightbox.photo.concertLocation">{{ lightbox.photo.concertLocation }}</template>
            </p>
          </div>
          
          <div class="lightbox-navigation">
            <button 
              class="nav-button prev" 
              @click="navigateLightbox(-1)"
              :disabled="currentPhotoIndex === 0"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div class="nav-counter">{{ currentPhotoIndex + 1 }} / {{ photos.length }}</div>
            <button 
              class="nav-button next" 
              @click="navigateLightbox(1)"
              :disabled="currentPhotoIndex === photos.length - 1"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FanPhotoGallery',
  data() {
    return {
      photos: [],
      loading: true,
      error: false,
      lightbox: {
        visible: false,
        photo: null
      },
      currentPhotoIndex: 0
    }
  },
  mounted() {
    this.loadPhotos()
  },
  methods: {
    async loadPhotos() {
      this.loading = true
      this.error = false
      
      try {
        const response = await fetch('/api/fan_photos.php')
        const data = await response.json()
        
        if (data.success) {
          this.photos = data.photos
        } else {
          this.error = true
          console.error('Fehler beim Laden der Fotos:', data.error)
        }
      } catch (error) {
        this.error = true
        console.error('Netzwerkfehler:', error)
      } finally {
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      
      try {
        const date = new Date(dateString)
        
        // Überprüfen, ob das Datum gültig ist
        if (isNaN(date.getTime())) {
          return ''
        }
        
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date)
      } catch (error) {
        console.error('Fehler bei der Datumsformatierung:', error, dateString)
        return ''
      }
    },
    
    openLightbox(photo) {
      this.lightbox.photo = photo
      this.lightbox.visible = true
      this.currentPhotoIndex = this.photos.findIndex(p => p.id === photo.id)
      
      // Scroll verhindern, wenn Lightbox offen ist
      document.body.style.overflow = 'hidden'
    },
    
    closeLightbox() {
      this.lightbox.visible = false
      this.lightbox.photo = null
      
      // Scroll wieder erlauben
      document.body.style.overflow = ''
    },
    
    navigateLightbox(direction) {
      const newIndex = this.currentPhotoIndex + direction
      
      if (newIndex >= 0 && newIndex < this.photos.length) {
        this.currentPhotoIndex = newIndex
        this.lightbox.photo = this.photos[newIndex]
      }
    }
  }
}
</script>

<style scoped>
.fan-photos {
  padding: 60px 0;
  background-color: var(--bg-alt);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 20px;
}

.section-header h2 {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  font-family: var(--font-family-serif);
  font-weight: 600;
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  margin: 1rem auto;
}

.celtic-divider span {
  height: 1px;
  width: 100px;
  background-color: var(--primary-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 1rem;
}

.intro {
  text-align: center;
  margin-bottom: 30px;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: var(--text-color);
}

.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 60px 20px;
  background-color: var(--white, #fff);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 132, 61, 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color, #00843d);
  animation: spin 1s infinite linear;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  color: var(--error-color, #f44336);
  margin-bottom: 20px;
}

.btn-retry {
  background-color: var(--primary-color, #00843d);
  color: var(--white, #fff);
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
}

.btn-retry:hover {
  background-color: var(--primary-dark, #006e33);
}

.empty-icon {
  font-size: 60px;
  color: var(--text-muted, #bbb);
  margin-bottom: 20px;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.photo-item {
  background-color: var(--white, #fff);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.photo-item:hover {
  transform: translateY(-5px);
}

.photo-image {
  position: relative;
  padding-bottom: 75%; /* 4:3 ratio */
  overflow: hidden;
}

.photo-image img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-item:hover .photo-image img {
  transform: scale(1.05);
}

.photo-info {
  padding: 15px;
}

.photo-caption {
  font-weight: 500;
  margin: 0 0 5px 0;
  color: var(--text-color, #333);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.photo-meta {
  font-size: 0.9rem;
  color: var(--text-muted, #777);
  margin: 0;
}

/* Lightbox Styles */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-content {
  max-width: 90%;
  max-height: 90%;
  position: relative;
  background-color: var(--white, #fff);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.lightbox-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(0, 0, 0, 0.6);
  color: var(--white, #fff);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #000;
}

.lightbox-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.lightbox-info {
  padding: 20px;
  background-color: var(--white, #fff);
}

.lightbox-info h3 {
  margin-top: 0;
  margin-bottom: 10px;
  color: var(--text-color, #333);
}

.lightbox-info p {
  color: var(--text-muted, #666);
  margin: 0;
}

.lightbox-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: var(--bg-light, #f8f8f8);
  border-top: 1px solid var(--border-color, #eee);
}

.nav-button {
  background-color: var(--white, #fff);
  color: var(--text-color, #333);
  border: 1px solid var(--border-color, #ddd);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
}

.nav-button:hover:not(:disabled) {
  background-color: var(--bg-light, #f4f4f4);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-counter {
  font-size: 14px;
  color: var(--text-muted, #666);
}

@media (max-width: 768px) {
  .photos-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
  }
  
  .lightbox-content {
    max-width: 95%;
    max-height: 95%;
  }
  
  .lightbox-image {
    max-height: 60vh;
  }
}
</style> 